// Article bodies used to be admin-only HTML, rendered as-is. Club managers
// can write news now too, and their HTML is shown on the club's site and
// anyone else's browser, so every body goes through this allowlist first.
//
// It rebuilds the document from scratch -- only allowed tags, only an
// http(s)/mailto href on links, text copied as text -- instead of trying to
// strip bad bits out of the original. Nothing the author wrote is ever
// parsed back as HTML, so there's no script, event handler, style or
// iframe that can survive. It covers everything RichTextEditor produces.

const ALLOWED_TAGS = new Set([
  "P", "BR", "B", "STRONG", "I", "EM", "U", "S", "H2", "H3", "H4",
  "UL", "OL", "LI", "BLOCKQUOTE", "A", "DIV", "SPAN", "HR",
]);
// Dropped together with everything inside them, not just unwrapped.
const DROP_WITH_CONTENT = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE", "NOSCRIPT", "SVG", "MATH"]);

function safeHref(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function copyChildren(from: Node, to: Node, doc: Document): void {
  from.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      to.appendChild(doc.createTextNode(child.textContent ?? ""));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const el = child as Element;
    const tag = el.tagName.toUpperCase();
    if (DROP_WITH_CONTENT.has(tag)) return;
    if (!ALLOWED_TAGS.has(tag)) {
      copyChildren(el, to, doc); // unknown wrapper: keep its text
      return;
    }
    const out = doc.createElement(tag);
    if (tag === "A") {
      const href = safeHref(el.getAttribute("href"));
      if (href) {
        out.setAttribute("href", href);
        out.setAttribute("target", "_blank");
        out.setAttribute("rel", "noopener noreferrer nofollow");
      }
    }
    copyChildren(el, out, doc);
    to.appendChild(out);
  });
}

/** Returns HTML that's safe to put in dangerouslySetInnerHTML. */
export function sanitizeArticleHtml(html: string): string {
  // DOMParser documents are inert: scripts don't run and images don't load.
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const out = document.implementation.createHTMLDocument("");
  const root = out.createElement("div");
  copyChildren(parsed.body, root, out);
  return root.innerHTML;
}
